if (!customElements.get('norst-balance-age')) {
  class NorstBalanceAge extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('[data-balance-age-form]');
      this.questions = Array.from(this.querySelectorAll('[data-balance-age-question]'));
      this.assessment = this.querySelector('[data-balance-age-assessment]');
      this.results = this.querySelector('[data-balance-age-results]');
      this.currentStep = 0;

      if (!this.form || this.questions.length === 0) return;

      this.startButton = this.querySelector('[data-balance-age-start]');
      this.backButton = this.querySelector('[data-balance-age-back]');
      this.nextButton = this.querySelector('[data-balance-age-next]');
      this.submitButton = this.querySelector('[data-balance-age-submit]');
      this.restartButton = this.querySelector('[data-balance-age-restart]');
      this.error = this.querySelector('[data-balance-age-error]');
      this.progress = this.querySelector('[data-balance-age-progress]');
      this.progressBar = this.querySelector('[data-balance-age-progress-bar]');
      this.stepCount = this.querySelector('[data-balance-age-step-count]');

      this.startButton?.addEventListener('click', () => this.start());
      this.backButton?.addEventListener('click', () => this.changeStep(-1));
      this.nextButton?.addEventListener('click', () => this.changeStep(1));
      this.restartButton?.addEventListener('click', () => this.restart());
      this.form.addEventListener('submit', (event) => this.complete(event));
      this.form.addEventListener('change', () => this.hideError());

      this.showStep(0, false);
    }

    start() {
      this.assessment.scrollIntoView({ behavior: this.reducedMotion() ? 'auto' : 'smooth', block: 'start' });
      window.setTimeout(() => this.focusQuestion(), this.reducedMotion() ? 0 : 350);
    }

    changeStep(direction) {
      if (direction > 0 && !this.currentQuestionAnswered()) {
        this.showError();
        return;
      }

      this.currentStep = Math.max(0, Math.min(this.questions.length - 1, this.currentStep + direction));
      this.showStep(this.currentStep, true);
    }

    showStep(index, shouldFocus) {
      this.questions.forEach((question, questionIndex) => {
        question.hidden = questionIndex !== index;
      });

      const stepNumber = index + 1;
      const progressPercent = (stepNumber / this.questions.length) * 100;

      this.progress?.setAttribute('aria-valuenow', String(stepNumber));
      if (this.progressBar) this.progressBar.style.width = `${progressPercent}%`;
      if (this.stepCount) this.stepCount.textContent = `${stepNumber} / ${this.questions.length}`;
      if (this.backButton) this.backButton.hidden = index === 0;
      if (this.nextButton) this.nextButton.hidden = index === this.questions.length - 1;
      if (this.submitButton) this.submitButton.hidden = index !== this.questions.length - 1;

      this.hideError();
      if (shouldFocus) this.focusQuestion();
    }

    focusQuestion() {
      const legend = this.questions[this.currentStep]?.querySelector('legend');
      if (!legend) return;
      legend.setAttribute('tabindex', '-1');
      legend.focus({ preventScroll: true });
    }

    currentQuestionAnswered() {
      return Boolean(this.questions[this.currentStep]?.querySelector('input:checked'));
    }

    showError() {
      if (!this.error) return;
      this.error.hidden = false;
      this.error.focus?.();
    }

    hideError() {
      if (this.error) this.error.hidden = true;
    }

    complete(event) {
      event.preventDefault();

      const firstUnanswered = this.questions.findIndex((question) => !question.querySelector('input:checked'));
      if (firstUnanswered !== -1) {
        this.currentStep = firstUnanswered;
        this.showStep(this.currentStep, true);
        this.showError();
        return;
      }

      const scoredAreas = ['movement', 'activity', 'confidence', 'eyesOpen', 'eyesClosed', 'practice'];
      const areaMaximums = {
        movement: 10,
        activity: 10,
        confidence: 15,
        eyesOpen: 20,
        eyesClosed: 20,
        practice: 15
      };
      const areaScores = {};
      let baseScore = 0;

      scoredAreas.forEach((name) => {
        const selected = this.form.querySelector(`input[name="${name}"]:checked`);
        const points = Number(selected?.dataset.points || 0);
        areaScores[name] = points;
        baseScore += points;
      });

      const consistencyInputs = ['confidence', 'eyesOpen', 'practice'];
      const consistency = consistencyInputs.reduce((total, name) => {
        return total + (areaScores[name] / areaMaximums[name]);
      }, 0) / consistencyInputs.length;
      const totalScore = Math.min(100, Math.round(baseScore + (consistency * 10)));

      let category = 'starting';
      if (totalScore >= 85) category = 'exceptional';
      else if (totalScore >= 70) category = 'strong';
      else if (totalScore >= 55) category = 'steady';
      else if (totalScore >= 35) category = 'developing';

      const focusArea = scoredAreas.reduce((lowest, name) => {
        const normalized = areaScores[name] / areaMaximums[name];
        return normalized < lowest.value ? { name, value: normalized } : lowest;
      }, { name: scoredAreas[0], value: Infinity }).name;

      this.renderResult(category, focusArea);
    }

    renderResult(category, focusArea) {
      const template = this.querySelector(`template[data-category="${category}"]`);
      const focusTemplate = this.querySelector(`template[data-focus="${focusArea}"]`);
      const recommendations = this.querySelector('[data-balance-age-recommendations]');
      const summary = template?.content.querySelector('[data-summary]');

      if (!template || !recommendations) return;

      this.querySelector('[data-balance-age-band]').textContent = template.dataset.band || '';
      this.querySelector('[data-balance-age-category]').textContent = template.dataset.title || '';
      this.querySelector('[data-balance-age-summary]').textContent = summary?.textContent.trim() || '';
      this.querySelector('[data-balance-age-focus]').textContent = focusTemplate?.content.textContent.trim() || '';

      recommendations.replaceChildren();
      template.content.querySelectorAll('article').forEach((article) => {
        recommendations.appendChild(article.cloneNode(true));
      });

      this.results.hidden = false;
      this.results.scrollIntoView({ behavior: this.reducedMotion() ? 'auto' : 'smooth', block: 'start' });
      window.setTimeout(() => {
        this.querySelector('[data-balance-age-result-focus]')?.focus({ preventScroll: true });
      }, this.reducedMotion() ? 0 : 350);
    }

    restart() {
      this.form.reset();
      this.currentStep = 0;
      this.results.hidden = true;
      this.showStep(0, false);
      this.assessment.scrollIntoView({ behavior: this.reducedMotion() ? 'auto' : 'smooth', block: 'start' });
      window.setTimeout(() => this.focusQuestion(), this.reducedMotion() ? 0 : 350);
    }

    reducedMotion() {
      return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    }
  }

  customElements.define('norst-balance-age', NorstBalanceAge);
}
